import React, { useState, useEffect, useRef, useCallback } from "react";
import { getDeviceLive } from "../../services/api";

import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Activity, Clock, Thermometer, Heart, X } from "lucide-react";
import { Button } from "../ui/button";

const PHASE_COLOURS = {
  Work: "bg-orange-500 text-white",
  Rest: "bg-sky-500 text-white",
  Warm: "bg-amber-400 text-black",
  Cool: "bg-teal-500 text-white",
  Prep: "bg-violet-500 text-white",
  RndRest: "bg-indigo-500 text-white",
  Paused: "bg-zinc-500 text-white",
  Active: "bg-green-500 text-white",
};

const formatTime = (totalSeconds) => {
  if (totalSeconds == null) return "--:--";
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const DeviceLiveView = ({ deviceId, onClose }) => {
  const [heartbeat, setHeartbeat] = useState(null);
  const [error, setError] = useState(false);
  const intervalRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const data = await getDeviceLive(deviceId);
      setHeartbeat(data);
      setError(false);
    } catch {
      setError(true);
    }
  }, [deviceId]);

  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, 2000);
    return () => clearInterval(intervalRef.current);
  }, [poll]);

  if (error) {
    return (
      <Card className="w-full border-destructive/50">
        <CardContent className="py-4 text-center text-sm text-muted-foreground">
          Unable to reach device.
        </CardContent>
      </Card>
    );
  }

  if (!heartbeat || heartbeat.state === "Inactive" || !heartbeat.state) {
    return (
      <Card className="w-full">
        <CardContent className="py-6 text-center">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Live view</span>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
          <Activity className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Device is idle</p>
        </CardContent>
      </Card>
    );
  }

  const hb = heartbeat;
  const phaseClass = PHASE_COLOURS[hb.state] || "bg-zinc-600 text-white";

  return (
    <Card className="w-full">
      <CardContent className="py-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={phaseClass}>{hb.state}</Badge>
            {hb.label && (
              <span className="text-sm font-medium">{hb.label}</span>
            )}
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>

        {/* Time */}
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="text-3xl font-mono font-bold tabular-nums">
              {formatTime(hb.remaining_s)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {formatTime(hb.phase_duration_s)} phase
            </div>
          </div>
        </div>

        {/* Progress */}
        <Progress value={(hb.progress || 0) * 100} className="h-2" />

        {/* Details row */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {hb.round != null && hb.total_rounds != null && (
            <span>
              Round {hb.round}/{hb.total_rounds}
            </span>
          )}
          {hb.session_elapsed_s != null && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(hb.session_elapsed_s)} total
            </span>
          )}
        </div>

        {/* Environment & HR */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {hb.env && (
            <span className="flex items-center gap-1">
              <Thermometer className="w-3 h-3" />
              {hb.env.temperature_c?.toFixed(1)}&deg;C &middot;{" "}
              {hb.env.humidity_pct?.toFixed(0)}%
            </span>
          )}
          {hb.hr_bpm != null && (
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3 text-red-500" />
              {hb.hr_bpm} bpm
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DeviceLiveView;
